/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { AnimatePresence, motion } from 'framer-motion'

import AlertBanner from '../AlertBanner'
import { AlertType } from '../../../../generated/graphql'
import { DetailsPlanAlert } from '../../../../types'

type PlanItemAlertsProps = {
  /** The alert to display */
  alerts: Array<DetailsPlanAlert>
  /** Label to use for course */
  courseLabel: string
  /** If the item is a placeholder */
  isCoursePlaceholder: boolean
  /** Whether the item has been locked */
  locked: boolean
  /** Event callback when the clear course selection button is clicked in the Alert Banner */
  onClear?: () => void
  /** Event callback when the delete button is clicked in the Alert Banner */
  onDelete?: () => void
  /** Event callback when the dismiss button is clicked in the Alert Banner */
  onDismissAlert?: (alert: DetailsPlanAlert) => void
  /** Whether the plan is read-only */
  readOnly: boolean
  /** Whether the item has been required */
  required: boolean
}

const defaultProps: Pick<PlanItemAlertsProps, 'onClear' | 'onDelete' | 'onDismissAlert'> = {
  onClear: undefined,
  onDelete: undefined,
  onDismissAlert: undefined
}

const bannerContainerCss = css`
  grid-column: 1 / 3;
  padding-bottom: 22px;

  div:not(:last-child) {
    padding-bottom: 8px;
  }
`

const PlanItemAlerts = ({
  alerts,
  courseLabel,
  isCoursePlaceholder,
  locked,
  onClear,
  onDelete,
  onDismissAlert,
  readOnly,
  required
}: PlanItemAlertsProps) => {
  const alertBanners = alerts.map((alert) => (
    <AlertBanner
      key={alert.alertKey}
      alert={alert}
      deleteButtonLabel={
        alert.alertType === AlertType.InactiveCourse ? `Delete ${courseLabel}` : 'Delete'
      }
      disableClear={readOnly}
      disableDelete={locked || readOnly || required}
      disableDismiss={readOnly}
      onClear={onClear}
      onDelete={onDelete}
      onDismiss={onDismissAlert}
      showClear={
        alert.alertType !== AlertType.Requisite &&
        alert.alertType !== AlertType.Unavailable &&
        alert.alertType !== AlertType.ItemMeetsRequirement &&
        alert.alertType !== AlertType.ItemDoesNotMeetRequirement &&
        alert.alertType !== AlertType.InProgressCourse &&
        isCoursePlaceholder
      }
      showDelete={
        alert.alertType !== AlertType.Requisite &&
        alert.alertType !== AlertType.Unavailable &&
        alert.alertType !== AlertType.ItemMeetsRequirement &&
        alert.alertType !== AlertType.ItemDoesNotMeetRequirement &&
        alert.alertType !== AlertType.InProgressCourse &&
        !isCoursePlaceholder
      }
      showDismiss={
        alert.alertType !== AlertType.InactiveCourse &&
        alert.alertType !== AlertType.ItemMeetsRequirement &&
        alert.alertType !== AlertType.ItemDoesNotMeetRequirement &&
        alert.alertType !== AlertType.InProgressCourse
      }
    />
  ))

  return (
    <AnimatePresence initial={false}>
      {!!alertBanners.length && (
        <motion.div
          key="alert_banner"
          initial={{ opacity: 0, scale: 0.3 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, y: -100, transition: { duration: 0.3 } }}
          css={bannerContainerCss}
        >
          {alertBanners}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

PlanItemAlerts.defaultProps = defaultProps

export default PlanItemAlerts
